import { useLanguage } from "../contexts/LanguageContext";
import { useAssimagraMetrics } from "../hooks/assimagra/useAssimagraMetrics";
import { MetricsCards } from "../components/assimagra/MetricsCards";
import { StoneTypePieChart } from "../components/assimagra/StoneTypePieChart";
import { ExtractionProductsPieChart } from "../components/assimagra/ExtractionProductsPieChart";
import { TransformationProductsPieChart } from "../components/assimagra/TransformationProductsPieChart";
import { FootprintComparisonCharts } from "../components/assimagra/FootprintComparisonCharts";
import { CompaniesDataTable } from "../components/assimagra/CompaniesDataTable";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { RefreshCw, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useData } from "../hooks/useData";
import { Company } from "../types/company/company.types";
import { useMemo } from "react";
import { FootprintDocument, FootprintProduct } from "../types/results";

export const AssimagraDashboard = () => {
  const { t } = useLanguage();
  const { metrics, companies, loading, error, refreshing, refreshMetrics, refetch } = useAssimagraMetrics();
  
  // Fetch full company data to get isArchived field
  const { data: allCompanies } = useData<Company>({
    collectionName: "companies",
  });

  // Fetch footprint data to recalculate metrics
  const { data: footprintDocuments } = useData<FootprintDocument>({
    collectionName: "footprints",
  });

  const { data: footprintProducts } = useData<FootprintProduct>({
    collectionName: "footprint_products",
  });
  
  // Filter archived companies from the companies with footprints data
  const activeCompanies = useMemo(() => {
    if (!companies || !allCompanies) return companies;
    
    // Create a map of company IDs to their archived status
    const companyArchivedMap = new Map<string, boolean>();
    allCompanies.forEach(company => {
      companyArchivedMap.set(company.id, company.isArchived || false);
    });
    
    // Filter out archived companies
    return companies.filter(company => !companyArchivedMap.get(company.id));
  }, [companies, allCompanies]);

  // Create filtered metrics that exclude archived companies from all calculations
  const filteredMetrics = useMemo(() => {
    if (!metrics || !activeCompanies || !footprintDocuments || !footprintProducts) return metrics;

    // Calculate filtered totals from active companies only
    const totalProductsCalculated = activeCompanies.reduce((sum, company) => sum + company.total_products_count, 0);
    
    // Filter the latest footprints to only include active companies
    const activeCompanyIds = new Set(activeCompanies.map(company => company.id));
    const filteredLatestFootprints = metrics.latest_footprints_by_company.filter(footprint => 
      activeCompanyIds.has(footprint.company_id)
    );

    // Get footprint documents for active companies only
    const activeFootprintDocuments = footprintDocuments.filter(doc => 
      activeCompanyIds.has(doc.company_id)
    );

    // Get footprint IDs for active companies
    const activeFootprintIds = new Set(activeFootprintDocuments.map(doc => doc.id));

    // Get products for active companies only
    const activeFootprintProducts = footprintProducts.filter(product => 
      activeFootprintIds.has(product.footprint_id)
    );

    // Recalculate stone types from active companies' products
    const stoneTypes = {
      limestone: 0,
      marble: 0,
      granite: 0,
      schist: 0,
      other: 0
    };

    // Helper function to categorize stone types
    const categorizeStoneType = (stoneTypeName: string | undefined): 'limestone' | 'marble' | 'granite' | 'schist' | 'other' => {
      if (!stoneTypeName) return 'other';
      
      const stoneType = stoneTypeName.toLowerCase().trim();
      
      // Limestone variations
      if (stoneType.includes('calcário') || stoneType.includes('limestone') || stoneType.includes('calcareo') || 
          stoneType.includes('calcario') || stoneType.includes('calcareous')) {
        return 'limestone';
      }
      
      // Marble variations
      if (stoneType.includes('mármore') || stoneType.includes('marble') || stoneType.includes('marmore') || 
          stoneType.includes('marmor')) {
        return 'marble';
      }
      
      // Granite variations
      if (stoneType.includes('granito') || stoneType.includes('granite') || stoneType.includes('granit')) {
        return 'granite';
      }
      
      // Schist/Shale variations
      if (stoneType.includes('xisto') || stoneType.includes('schist') || stoneType.includes('shale') || 
          stoneType.includes('xist') || stoneType.includes('esquisto')) {
        return 'schist';
      }
      
      return 'other';
    };

    // Count stone types from active companies' products
    activeFootprintProducts.forEach(product => {
      const stoneTypeName = product.stone_type_name;
      
      const category = categorizeStoneType(stoneTypeName);
      stoneTypes[category]++;
    });

    // Recalculate extraction and transformation products from active companies
    const extractionProducts: Record<string, number> = {};
    const transformationProducts: Record<string, number> = {};

    // Count products by activity type from active companies
    activeFootprintProducts.forEach(product => {
      const productType = product.product_type || 'Unknown';
      const productFlow = product.product_flow;
      
      if (productFlow === 'extraction') {
        extractionProducts[productType] = (extractionProducts[productType] || 0) + 1;
      }
      if (productFlow === 'transformation') {
        transformationProducts[productType] = (transformationProducts[productType] || 0) + 1;
      }
    });

    const filteredMetrics = {
      ...metrics,
      companies_with_footprints: {
        ...metrics.companies_with_footprints,
        count: activeCompanies.length,
        total_associates: metrics.companies_with_footprints.total_associates,
        percentage: activeCompanies.length > 0 ? (activeCompanies.length / metrics.companies_with_footprints.total_associates) * 100 : 0
      },
      total_products_calculated: totalProductsCalculated,
      latest_footprints_by_company: filteredLatestFootprints,
      stone_types: stoneTypes,
      extraction_products: extractionProducts,
      transformation_products: transformationProducts,
    };

    return filteredMetrics;
  }, [metrics, activeCompanies, footprintDocuments, footprintProducts]);

  if (loading) {
    return (
      <div className="container mx-auto p-4">
        <div className="space-y-6">
          <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded animate-pulse"></div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-80 bg-gray-200 rounded animate-pulse"></div>
            ))}
          </div>
          <div className="h-96 bg-gray-200 rounded animate-pulse"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-4">
        <div className="space-y-6">
          <h1 className="text-2xl font-bold text-gray-900">{t("assimagraDashboard.title")}</h1>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <Card>
            <CardContent className="flex items-center justify-center py-12">
              <div className="text-center">
                <p className="text-gray-500 mb-4">{t("assimagraDashboard.error.description")}</p>
                <Button onClick={refetch} variant="outline">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {t("assimagraDashboard.error.tryAgain")}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="container mx-auto p-4">
        <div className="space-y-6">
          <h1 className="text-2xl font-bold text-gray-900">{t("assimagraDashboard.title")}</h1>
          <Card>
            <CardContent className="flex items-center justify-center py-12">
              <div className="text-center">
                <p className="text-gray-500 mb-4">{t("assimagraDashboard.noData.title")}</p>
                <Button onClick={refetch} variant="outline">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {t("assimagraDashboard.noData.loadData")}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t("assimagraDashboard.title")}</h1>
            <p className="text-gray-500">{t("assimagraDashboard.subtitle")}</p>
          </div>
          <Button onClick={refreshMetrics} disabled={refreshing} variant="outline" className="flex items-center gap-2">
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? t("assimagraDashboard.refreshing") : t("assimagraDashboard.refreshData")}
          </Button>
        </div>

        {/* KPI Cards */}
        <MetricsCards metrics={filteredMetrics} />

        {/* Charts Section */}
        <div className="space-y-6">
          {/* Pie Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <StoneTypePieChart metrics={filteredMetrics} />
            <ExtractionProductsPieChart metrics={filteredMetrics} />
            <TransformationProductsPieChart metrics={filteredMetrics} />
          </div>

          {/* Footprint Comparison Charts */}
          <FootprintComparisonCharts metrics={filteredMetrics} companies={activeCompanies} />
        </div>

        {/* Companies Table */}
        <CompaniesDataTable companies={activeCompanies} />
      </div>
    </div>
  );
};
